/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
export const DEFAULT: unique symbol = Symbol('Default')

/**
 * A match arm for a value.
 *
 * @param R The return type of the arm
 */
type MatchArm<R> = R | (() => R)

/**
 * A match arm for a tagged union variant.
 *
 * @param T The type of the object
 * @param P The property name
 * @param K The type of the property value
 * @param R The return type of the arm
 */
type TaggedUnionMatchArm<
  T extends Record<P, string | number | symbol>,
  P extends keyof T,
  K,
  R,
> = (value: Extract<T, Record<P, K>>) => R

/**
 * The cases for a pattern match.
 *
 * @param T The type of the object
 * @param P The property name
 * @param R The return type of the arm
 */
type TaggedUnionCases<
  T extends Record<P, string | number | symbol>,
  P extends keyof T,
  R,
> =
  | { [K in T[P]]: TaggedUnionMatchArm<T, P, K, R> }
  | ({
      [K in T[P]]?: TaggedUnionMatchArm<T, P, K, R>
    } & {
      [DEFAULT]: () => R
    })

/**
 * Performs an exhaustive pattern match on a property of the specified object.
 * This overload supports function arms that receive the correctly typed object.
 *
 * @param obj The object containing the property to match on
 * @param matchOn The property name to match on
 * @param cases The cases to match against
 */
export function match<
  T extends Record<P, string | number | symbol>,
  P extends keyof T,
  C extends TaggedUnionCases<T, P, unknown>,
>(obj: T, matchOn: P, cases: C): ReturnType<Exclude<C[keyof C], undefined>>

/**
 * Performs an exhaustive pattern match on the specified input.
 *
 * @param matchOn The value to match against
 * @param cases The cases to match against
 */
export function match<const K extends string | number | symbol, R>(
  matchOn: K,
  cases:
    | Record<K, MatchArm<R>>
    | (Partial<Record<K, MatchArm<R>>> & { [DEFAULT]: MatchArm<R> }),
): R

// Implementation that handles both overloads
export function match(
  objOrMatchOn: any,
  casesOrProp: any,
  maybeCases?: any,
): any {
  // Check if we're using the object property pattern matching overload
  if (
    maybeCases !== undefined &&
    typeof objOrMatchOn === 'object' &&
    typeof casesOrProp === 'string'
  ) {
    // Handle object property pattern matching
    const obj = objOrMatchOn
    const prop = casesOrProp
    const cases = maybeCases

    const discriminator = obj[prop]

    // If there's no specific case for the discriminator value,
    // check if there's a DEFAULT case
    if (!(discriminator in cases)) {
      if (DEFAULT in cases) {
        const defaultArm = cases[DEFAULT]
        return defaultArm()
      }

      return noMatch(discriminator)()
    }

    const caseArm = cases[discriminator]
    return caseArm(obj)
  }

  // Match directly on the value against the cases
  const matchOn = objOrMatchOn
  const cases = casesOrProp as Record<string | number | symbol, any>

  if (!(matchOn in cases)) {
    if (DEFAULT in cases) {
      const defaultArm = cases[DEFAULT]
      return isFunctionArm(defaultArm) ? defaultArm(matchOn) : defaultArm
    }

    return noMatch(matchOn)()
  }

  const arm = cases[matchOn]

  return isFunctionArm(arm) ? arm() : arm
}

/**
 * Checks if the specified arm is a function.
 * @param arm
 */
function isFunctionArm<R>(arm: MatchArm<R>): arm is (...args: any[]) => R {
  return typeof arm === 'function'
}

function noMatch(matchOn: unknown): (...args: unknown[]) => never {
  return () => {
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    throw new Error(`No match for ${matchOn}`)
  }
}

match.DEFAULT = DEFAULT as typeof DEFAULT
